import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import {
	PageIncludeByIdQuery,
	type PageIncludeByIdQuery$data,
	type PageIncludeByIdQuery$variables,
} from '@confluence/page-include-macro/entry-points/PageIncludeByIdQuery.ts';
import {
	PageIncludeByTitleQuery,
	type PageIncludeByTitleQuery$data,
	type PageIncludeByTitleQuery$variables,
} from '@confluence/page-include-macro/entry-points/PageIncludeByTitleQuery.ts';
import {
	getPageIncludeQueryTarget,
	parseMacroTarget,
} from '@confluence/page-include-macro/entry-points/pageIncludeMacroParams.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	MacroContentNode,
	MacroPreloadContext,
	MacroPreloadHandler,
} from '../preloadMacroData.ts';

const preloadById = (variables: PageIncludeByIdQuery$variables) =>
	slowQueryGuard(
		'pageIncludeByIdPreload',
		() =>
			query<PageIncludeByIdQuery$data, PageIncludeByIdQuery$variables>({
				query: PageIncludeByIdQuery,
				variables,
				context: { single: true },
				...(expValEquals('cc_include_ssr_preload_cls', 'isEnabled', true)
					? { relayQueryPreloadRetention: 'environment' as const }
					: {}),
			}),
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

const preloadByTitle = (variables: PageIncludeByTitleQuery$variables) =>
	slowQueryGuard(
		'pageIncludeByTitlePreload',
		() =>
			query<PageIncludeByTitleQuery$data, PageIncludeByTitleQuery$variables>({
				query: PageIncludeByTitleQuery,
				variables,
				context: { single: true },
				...(expValEquals('cc_include_ssr_preload_cls', 'isEnabled', true)
					? { relayQueryPreloadRetention: 'environment' as const }
					: {}),
			}),
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

export const pageIncludePreloadHandler: MacroPreloadHandler = {
	extensionKey: 'include',

	routes: ['view', 'space-overview', 'edit', 'live'],

	// Include is excluded from the generic macro-rendering query because the client uses the native
	// renderer. Keep its native Relay result available across SSR and hydration instead.
	ignoreSsrRenderBlocklist: true,

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const parsedTarget = parseMacroTarget(node.attrs?.parameters?.macroParams);
		const queryTarget = getPageIncludeQueryTarget(parsedTarget, context.spaceKey);

		if (queryTarget.kind === 'by-id') {
			await preloadById(queryTarget.variables);
			return;
		}

		if (queryTarget.kind === 'by-title') {
			await preloadByTitle(queryTarget.variables);
		}
	},
};
